import { useState, useEffect, useCallback } from "react";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import Modal from "../../components/ui/Modal";
import { adminApi } from "../../api/admin";
import useAuthStore from "../../store/authStore";

function EntityBadge({ entity, entities }) {
  const label = entities?.find((c) => c.slug === entity)?.name ?? entity ?? "—";
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-gray-50 text-gray-600 border-gray-200">
      {label}
    </span>
  );
}

const PROVIDER_LABELS = {
  google:    { label: "Google",    cls: "bg-blue-50 text-blue-700 border-blue-200" },
  microsoft: { label: "Microsoft", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
};

function ProviderBadge({ provider }) {
  if (!provider) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-gray-50 text-gray-600 border-gray-200">
        Local
      </span>
    );
  }
  const p = PROVIDER_LABELS[provider] ?? { label: provider, cls: "bg-gray-50 text-gray-600 border-gray-200" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${p.cls}`}>
      {p.label}
    </span>
  );
}

function UserAvatar({ user }) {
  const initial = (user.full_name?.[0] || user.username?.[0] || "?").toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full bg-bh-500 flex items-center justify-center text-sm font-semibold text-white shrink-0">
      {initial}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

// ── Create modal ─────────────────────────────────────────────────────────────

const EMPTY_CREATE = { email: "", username: "", full_name: "", password: "", is_admin: false, entity: "fastblade" };

function CreateUserModal({ open, onClose, onCreated, entities }) {
  const [form, setForm] = useState(EMPTY_CREATE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setForm(EMPTY_CREATE); setError(null); } }, [open]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const { data } = await adminApi.createUser(form);
      onCreated(data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add User">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Full name" value={form.full_name} onChange={set("full_name")} />
          <Field label="Email *"   value={form.email}     onChange={set("email")} type="email" required />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Username *"   value={form.username} onChange={set("username")} required />
          <Field label="Password *"   value={form.password} onChange={set("password")} type="password"
            placeholder="min. 8 characters" required />
        </div>
        <EntitySelect value={form.entity} onChange={set("entity")} entities={entities} />
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <input type="checkbox" checked={form.is_admin} onChange={set("is_admin")}
            className="rounded border-gray-300 text-bh-600 focus:ring-bh-500 accent-bh-600" />
          Grant admin rights
        </label>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={saving}
            className="px-4 py-2 bg-bh-600 text-white rounded-lg text-sm font-medium hover:bg-bh-700 disabled:opacity-50 transition-colors">
            {saving ? "Creating…" : "Create user"}
          </button>
          <button type="button" onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Edit modal ────────────────────────────────────────────────────────────────

function EditUserModal({ user, open, onClose, onUpdated, currentUserId, entities }) {
  const [form, setForm] = useState({});
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && user) {
      setForm({ full_name: user.full_name || "", is_active: user.is_active, is_admin: user.is_admin, entity: user.entity || "fastblade" });
      setNewPassword("");
      setError(null);
    }
  }, [open, user]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const payload = { ...form };
      if (newPassword) payload.password = newPassword;
      const { data } = await adminApi.updateUser(user.id, payload);
      onUpdated(data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;
  const isSelf = user.id === currentUserId;
  const isSSO  = !!user.oauth_provider;

  return (
    <Modal open={open} onClose={onClose} title={`Edit — ${user.full_name || user.username}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Full name" value={form.full_name ?? ""} onChange={set("full_name")} />

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Email</p>
            <p className="text-sm text-gray-700">{user.email}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Username</p>
            <p className="text-sm text-gray-700">{user.username}</p>
          </div>
        </div>

        <EntitySelect value={form.entity ?? "fastblade"} onChange={set("entity")} entities={entities} />

        <div className="flex gap-6">
          <label className={`flex items-center gap-2 text-sm cursor-pointer select-none ${isSelf ? "opacity-40 cursor-not-allowed" : "text-gray-700"}`}>
            <input type="checkbox" checked={!!form.is_active} onChange={set("is_active")}
              disabled={isSelf}
              className="rounded border-gray-300 accent-bh-600" />
            Active
          </label>
          <label className={`flex items-center gap-2 text-sm cursor-pointer select-none ${isSelf ? "opacity-40 cursor-not-allowed" : "text-gray-700"}`}>
            <input type="checkbox" checked={!!form.is_admin} onChange={set("is_admin")}
              disabled={isSelf}
              className="rounded border-gray-300 accent-bh-600" />
            Admin
          </label>
        </div>

        {!isSSO && (
          <Field label="New password (leave blank to keep current)" value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)} type="password"
            placeholder="min. 8 characters" />
        )}
        {isSSO && (
          <p className="text-xs text-gray-400">
            Password cannot be set for {PROVIDER_LABELS[user.oauth_provider]?.label ?? user.oauth_provider} SSO users.
          </p>
        )}

        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={saving}
            className="px-4 py-2 bg-bh-600 text-white rounded-lg text-sm font-medium hover:bg-bh-700 disabled:opacity-50 transition-colors">
            {saving ? "Saving…" : "Save changes"}
          </button>
          <button type="button" onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Field helper ──────────────────────────────────────────────────────────────

function Field({ label, value, onChange, type = "text", placeholder, required }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-500 block mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-bh-400 focus:border-transparent"
      />
    </label>
  );
}

function EntitySelect({ value, onChange, entities }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-500 block mb-1">Entity</span>
      <select
        value={value}
        onChange={onChange}
        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-bh-400 focus:border-transparent bg-white"
      >
        {(entities ?? []).map((c) => (
          <option key={c.slug} value={c.slug}>{c.name}</option>
        ))}
      </select>
    </label>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AdminUsersPage() {
  const { user: currentUser } = useAuthStore();
  const [users,    setUsers]    = useState([]);
  const [entities, setEntities] = useState([]);
  const [filter,   setFilter]   = useState("");
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, entitiesRes] = await Promise.all([
        adminApi.listUsers(),
        adminApi.listEntities(),
      ]);
      setUsers(usersRes.data);
      setEntities(entitiesRes.data);
    } catch {
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (u) => setUsers((prev) => [...prev, u]);

  const handleUpdated = (u) => setUsers((prev) => prev.map((x) => (x.id === u.id ? u : x)));

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.full_name || u.username}"? This cannot be undone.`)) return;
    try {
      await adminApi.deleteUser(u.id);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (err) {
      alert(err?.response?.data?.error || "Delete failed");
    }
  };

  const visible = users.filter((u) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      u.email.toLowerCase().includes(q) ||
      u.username.toLowerCase().includes(q) ||
      (u.full_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-6 max-w-6xl space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">User Management</h1>
          <p className="text-sm text-gray-500 mt-0.5">{users.length} user{users.length !== 1 ? "s" : ""} registered</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-bh-600 text-white rounded-lg text-sm font-medium hover:bg-bh-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          Add user
        </button>
      </div>

      <input
        type="search"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter by name, email or username…"
        className="w-full max-w-sm text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-bh-400 focus:border-transparent"
      />

      {error && <p className="text-sm text-red-600">{error}</p>}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Username</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Entity</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Auth</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Last login</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-400">
                    {filter ? "No users match that filter." : "No users yet."}
                  </td>
                </tr>
              )}
              {visible.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <UserAvatar user={u} />
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{u.full_name || "—"}</p>
                        <p className="text-xs text-gray-400 truncate">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs">{u.username}</td>
                  <td className="px-4 py-3">
                    <EntityBadge entity={u.entity} entities={entities} />
                  </td>
                  <td className="px-4 py-3">
                    <ProviderBadge provider={u.oauth_provider} />
                  </td>
                  <td className="px-4 py-3">
                    {u.is_admin && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-bh-50 text-bh-700 border-bh-200">
                        Admin
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.is_active ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />Disabled
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{fmtDate(u.last_login)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => setEditTarget(u)}
                        className="p-1.5 rounded text-gray-400 hover:text-bh-600 hover:bg-bh-50 transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(u)}
                        disabled={u.id === currentUser?.id}
                        className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title={u.id === currentUser?.id ? "Cannot delete your own account" : "Delete"}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        entities={entities}
      />
      <EditUserModal
        user={editTarget}
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        onUpdated={handleUpdated}
        currentUserId={currentUser?.id}
        entities={entities}
      />
    </div>
  );
}
